import React, { useState } from 'react';
import { XIcon, SearchIcon, Loader2Icon, CheckCircle2Icon } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { DraggableModal } from './ui/DraggableModal';
import { formatAmount } from '../utils/currencyFormat';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
interface POHeaderSearchModalProps {
  vendorName: string;
  onApply: (poNumber: string, matchingType: '2-way' | '3-way') => void;
  onClose: () => void;
}
// Mock PO data for header-level selection
interface MockPO {
  poNumber: string;
  poDate: string;
  supplier: string;
  totalAmount: number;
  currency: string;
  lineCount: number;
  hasGoodsReceipts: boolean;
  status: 'Open' | 'Partially Received' | 'Fully Received';
}
const MOCK_POS: MockPO[] = [{
  poNumber: 'PO00789',
  poDate: '2024-01-02',
  supplier: 'Dell Australia',
  totalAmount: 120000.0,
  currency: 'AUD',
  lineCount: 3,
  hasGoodsReceipts: true,
  status: 'Partially Received'
}, {
  poNumber: 'PO00856',
  poDate: '2024-01-15',
  supplier: 'Office Furniture Co',
  totalAmount: 20000.0,
  currency: 'AUD',
  lineCount: 3,
  hasGoodsReceipts: true,
  status: 'Fully Received'
}, {
  poNumber: 'PO00912',
  poDate: '2024-01-20',
  supplier: 'Office Furniture Co',
  totalAmount: 8000.0,
  currency: 'AUD',
  lineCount: 1,
  hasGoodsReceipts: false,
  status: 'Open'
}, {
  poNumber: 'PO01045',
  poDate: '2024-01-25',
  supplier: 'Tech Equipment Ltd',
  totalAmount: 13636.36,
  currency: 'AUD',
  lineCount: 5,
  hasGoodsReceipts: true,
  status: 'Partially Received'
}, {
  poNumber: 'PO01102',
  poDate: '2024-01-28',
  supplier: 'Office Supplies Co',
  totalAmount: 4500.0,
  currency: 'AUD',
  lineCount: 5,
  hasGoodsReceipts: false,
  status: 'Open'
}, {
  poNumber: 'PO01156',
  poDate: '2024-02-01',
  supplier: 'Tech Supplies Ltd',
  totalAmount: 3000.0,
  currency: 'AUD',
  lineCount: 1,
  hasGoodsReceipts: false,
  status: 'Open'
}];
export function POHeaderSearchModal({
  vendorName,
  onApply,
  onClose
}: POHeaderSearchModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPO, setSelectedPO] = useState<MockPO | null>(null);
  const [matchingType, setMatchingType] = useState<'2-way' | '3-way'>('2-way');
  // Keyboard shortcuts
  useKeyboardShortcuts({
    shortcuts: [{
      key: 'Escape',
      handler: onClose,
      description: 'Close modal'
    }],
    enabled: true
  });
  const handleSearch = () => {
    setIsSearching(true);
    // Simulate API call
    setTimeout(() => {
      setIsSearching(false);
    }, 500);
  };
  const handlePOSelect = (po: MockPO) => {
    setSelectedPO(po);
    // Auto-select matching type based on GR availability
    setMatchingType(po.hasGoodsReceipts ? '3-way' : '2-way');
  };
  const handleApply = () => {
    if (!selectedPO) return;
    onApply(selectedPO.poNumber, matchingType);
  };
  const filteredPOs = MOCK_POS.filter(po => po.supplier === vendorName && (po.poNumber.toLowerCase().includes(searchQuery.toLowerCase()) || po.status.toLowerCase().includes(searchQuery.toLowerCase())));
  return <DraggableModal isOpen={true} onClose={onClose} title="Select Purchase Order" defaultWidth={900} defaultHeight={600} minWidth={700} minHeight={400} data-id="element-1584">
      <div className="flex flex-col h-full" data-id="element-1585">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50 flex-shrink-0" data-id="element-1586">
          <div data-id="element-1587">
            <p className="text-sm font-medium text-gray-900" data-id="element-1588">
              Vendor: {vendorName}
            </p>
            <p className="text-xs text-gray-600 mt-0.5" data-id="element-1589">
              Select a PO to convert this invoice to a PO-based invoice
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-1590">
          {!selectedPO ? <>
              {/* Search */}
              <div className="mb-4" data-id="element-1591">
                <div className="flex gap-2" data-id="element-1592">
                  <div className="flex-1 relative" data-id="element-1593">
                    <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1594" />
                    <Input type="text" placeholder="Search by PO number or status..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" autoFocus data-id="element-1595" />
                  </div>
                  <Button onClick={handleSearch} disabled={isSearching} data-id="element-1596">
                    {isSearching ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-1597" /> : 'Search'}
                  </Button>
                </div>
              </div>

              {/* PO List */}
              <div className="space-y-2" data-id="element-1598">
                {filteredPOs.length === 0 ? <div className="text-center py-12" data-id="element-1599">
                    <p className="text-sm text-gray-500" data-id="element-1600">
                      No purchase orders found for this vendor
                    </p>
                  </div> : filteredPOs.map(po => <button key={po.poNumber} onClick={() => handlePOSelect(po)} className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all" data-id="element-1601">
                      <div className="flex items-start justify-between" data-id="element-1602">
                        <div className="flex-1" data-id="element-1603">
                          <div className="flex items-center gap-2 mb-2" data-id="element-1604">
                            <code className="text-sm font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded font-semibold" data-id="element-1605">
                              {po.poNumber}
                            </code>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded ${po.status === 'Open' ? 'bg-gray-100 text-gray-700' : po.status === 'Partially Received' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`} data-id="element-1606">
                              {po.status}
                            </span>
                            {po.hasGoodsReceipts && <span className="px-2 py-0.5 text-xs font-semibold rounded bg-green-100 text-green-700" data-id="element-1607">
                                Has GRs
                              </span>}
                          </div>
                          <div className="grid grid-cols-4 gap-4 text-xs" data-id="element-1608">
                            <div data-id="element-1609">
                              <div className="text-gray-600 mb-1" data-id="element-1610">PO Date</div>
                              <div className="font-semibold text-gray-900" data-id="element-1611">
                                {po.poDate}
                              </div>
                            </div>
                            <div data-id="element-1612">
                              <div className="text-gray-600 mb-1" data-id="element-1613">
                                Total Amount
                              </div>
                              <div className="font-semibold text-gray-900" data-id="element-1614">
                                {formatAmount(po.totalAmount, po.currency)}
                              </div>
                            </div>
                            <div data-id="element-1615">
                              <div className="text-gray-600 mb-1" data-id="element-1616">
                                Line Items
                              </div>
                              <div className="font-semibold text-gray-900" data-id="element-1617">
                                {po.lineCount}
                              </div>
                            </div>
                            <div data-id="element-1618">
                              <div className="text-gray-600 mb-1" data-id="element-1619">Currency</div>
                              <div className="font-semibold text-gray-900" data-id="element-1620">
                                {po.currency}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>)}
              </div>
            </> : <>
              {/* Selected PO Details */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-1621">
                <div className="flex items-start justify-between mb-3" data-id="element-1622">
                  <div className="flex items-center gap-2" data-id="element-1623">
                    <code className="text-sm font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded border border-blue-200 font-semibold" data-id="element-1624">
                      {selectedPO.poNumber}
                    </code>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${selectedPO.status === 'Open' ? 'bg-gray-100 text-gray-700' : selectedPO.status === 'Partially Received' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`} data-id="element-1625">
                      {selectedPO.status}
                    </span>
                    {selectedPO.hasGoodsReceipts && <span className="px-2 py-0.5 text-xs font-semibold rounded bg-green-100 text-green-700" data-id="element-1626">
                        Has GRs
                      </span>}
                  </div>
                  <button onClick={() => setSelectedPO(null)} className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium" data-id="element-1627">
                    Change PO
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-4 text-xs" data-id="element-1628">
                  <div data-id="element-1629">
                    <div className="text-blue-700 mb-1" data-id="element-1630">PO Date</div>
                    <div className="font-semibold text-gray-900" data-id="element-1631">
                      {selectedPO.poDate}
                    </div>
                  </div>
                  <div data-id="element-1632">
                    <div className="text-blue-700 mb-1" data-id="element-1633">Total Amount</div>
                    <div className="font-semibold text-gray-900" data-id="element-1634">
                      {formatAmount(selectedPO.totalAmount, selectedPO.currency)}
                    </div>
                  </div>
                  <div data-id="element-1635">
                    <div className="text-blue-700 mb-1" data-id="element-1636">Line Items</div>
                    <div className="font-semibold text-gray-900" data-id="element-1637">
                      {selectedPO.lineCount}
                    </div>
                  </div>
                  <div data-id="element-1638">
                    <div className="text-blue-700 mb-1" data-id="element-1639">Currency</div>
                    <div className="font-semibold text-gray-900" data-id="element-1640">
                      {selectedPO.currency}
                    </div>
                  </div>
                </div>
              </div>

              {/* Matching Type Selection */}
              <div className="border border-gray-200 rounded-lg p-4 mb-6" data-id="element-1641">
                <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-1642">
                  Select Matching Type
                </h3>
                <div className="space-y-3" data-id="element-1643">
                  <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors" data-id="element-1644">
                    <input type="radio" name="matchingType" value="2-way" checked={matchingType === '2-way'} onChange={() => setMatchingType('2-way')} className="mt-0.5" data-id="element-1645" />
                    <div className="flex-1" data-id="element-1646">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1647">
                        <span className="text-sm font-semibold text-gray-900" data-id="element-1648">
                          2-Way Match
                        </span>
                        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-700" data-id="element-1649">
                          Invoice ↔ PO
                        </span>
                      </div>
                      <p className="text-xs text-gray-600" data-id="element-1650">
                        Match invoice lines directly to purchase order lines. No
                        goods receipt verification required.
                      </p>
                    </div>
                  </label>

                  <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${selectedPO.hasGoodsReceipts ? 'border-gray-200 hover:bg-gray-50' : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'}`} data-id="element-1651">
                    <input type="radio" name="matchingType" value="3-way" checked={matchingType === '3-way'} onChange={() => setMatchingType('3-way')} disabled={!selectedPO.hasGoodsReceipts} className="mt-0.5" data-id="element-1652" />
                    <div className="flex-1" data-id="element-1653">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1654">
                        <span className="text-sm font-semibold text-gray-900" data-id="element-1655">
                          3-Way Match
                        </span>
                        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-green-100 text-green-700" data-id="element-1656">
                          Invoice ↔ PO ↔ GR
                        </span>
                        {!selectedPO.hasGoodsReceipts && <span className="px-2 py-0.5 text-xs font-semibold rounded bg-gray-100 text-gray-600" data-id="element-1657">
                            No GRs Available
                          </span>}
                      </div>
                      <p className="text-xs text-gray-600" data-id="element-1658">
                        Match invoice lines to purchase order and verify against
                        goods receipts. Ensures physical receipt confirmation.
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Info Banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-1659">
                <div className="flex items-start gap-3" data-id="element-1660">
                  <CheckCircle2Icon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1661" />
                  <div data-id="element-1662">
                    <div className="text-sm font-semibold text-amber-900 mb-1" data-id="element-1663">
                      Converting to PO Invoice
                    </div>
                    <p className="text-xs text-amber-700" data-id="element-1664">
                      This will set the PO number in the document header and
                      convert this invoice to a{' '}
                      {matchingType === '2-way' ? '2-Way' : '3-Way'} matching
                      invoice. You'll be able to match individual line items to
                      this PO in the line items section.
                    </p>
                  </div>
                </div>
              </div>
            </>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2 flex-shrink-0" data-id="element-1665">
          <Button variant="primary" onClick={handleApply} disabled={!selectedPO} data-id="element-1666">
            Apply PO Number
          </Button>
          <Button variant="ghost" onClick={onClose} data-id="element-1667">
            Cancel
          </Button>
        </div>
      </div>
    </DraggableModal>;
}